import { Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormGroup, Grid, Switch, TextField, Typography } from '@mui/material';
import { withStyles, WithStyles } from "@mui/styles";
import * as pubApi from "api/Knowledge";
import styles from "assets/jss/cardCreation.styles";
import { AppMessageBox } from 'components/AppMessageBox';
import ButtonSave from 'components/Buttons/ButtonSave';
import IconButtonClose from 'components/Buttons/IconButtonClose';
import { RequestIndicator } from 'components/CallServer';
import RequestErrorDialog from 'components/CallServer/RequestErrorDialog';
import { RequestErrorReport } from 'components/CallServer/RequestErrorReport';
import AttachSelection, { ConvertUploadToFileThumbnaildGetDto, UploadingStatus } from 'components/FileUpload/AttachSelection';
import MuiRegularButton from 'components/MuiExtras/Button/MuiRegularButton';
import MuiPushRight from 'components/MuiExtras/MuiPushRight';
import { withSnackbar, WithSnackbar } from 'components/SnackbarProvider/Snackbar';
import TextFieldCounter from 'components/TextFieldCounter';
import MediaUtils from 'lib/MediaUtils';
import { AspectRatio, PublicationEditPermissions, SystemRoles } from 'models/ApiEnums';
import { AsyncUploadModel, ContentMetadata, FileThumbnaildGetDto, GetListDto, SaveListDto, SavePublicationResult } from 'models/ApiModels';
import { AttachType, RequestLifeCycle } from 'models/AppModels';
import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from "react-redux";
import { Action, bindActionCreators } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { RoutePaths } from 'routes/RouteConfig';
import Session from 'services/Session';
import { ApplicationState } from "store/rootReducer";
import * as SideBarActions from 'store/sidebar/actions';
import PublicationAdvancedOptions, { IInstitutionalBoxValidation } from 'views/Publication/Components/PublicationAdvancedOptions';
import TopicsAndMembersBox from 'views/Publication/Components/TopicsAndMembersBox';
import { getPublicationSecondViewAction } from 'views/Timeline/Components/FeedHelpers';

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const MaxChars = 1500;

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    publicationId?: string;
    isPublic?: boolean;
    onClose: (result: SavePublicationResult) => void;
}

interface IValidation {
    title?: boolean;
    topics?: boolean;
}

interface IState {
    record: Partial<GetListDto>;
    validation: IValidation & IInstitutionalBoxValidation;

    step: number;
    selectedAttach?: AttachType;
    uploadingStatus: UploadingStatus;
    formCycle: RequestLifeCycle;
    lastError: any;
}

type IDispatchProps = typeof SideBarActions;

type Props = IProps & IDispatchProps & WithStyles<typeof styles> & WithTranslation & WithSnackbar;

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

class CreateList extends React.Component<Props, IState> {

    constructor(props: Props) {
        super(props);
        this.state = {
            record: {
                publicationId: null,
                contentBody: "",
                title: "",
                topics: [],
                relativesTopics: [],
                deletedRelativesTopics: [],
                attachs: null,
                advOptions: {
                    enabled: false,
                    isInstitutional: false,
                    viewPeriodStart: null,
                    viewPeriodEnd: null,
                    viewHighlightUntil: null,
                    viewPriority: null,
                },
                isPublic: props.isPublic || false,
                allowSuggestions: false,
                editPermissions: PublicationEditPermissions.Full
            },
            validation: {
                title: null,
                topics: null,
                hightLightUntil: null,
                period: null,
            },

            selectedAttach: null,
            step: 1,
            uploadingStatus: "none",

            formCycle: RequestLifeCycle.None,
            lastError: null
        };
    }

    ///
    private updateRecord(data: Partial<GetListDto>) {
        return { ...this.state.record, ...data };
    }

    ///
    private handleFileUploaded(e: AsyncUploadModel) {
        var data: FileThumbnaildGetDto = ConvertUploadToFileThumbnaildGetDto(e);
        var at = MediaUtils.getAttachTypeFromMimeType(data.mainFile.mimeType);

        if (at == AttachType.Image) {
            const files = this.state.record.attachs == null ? [] : this.state.record.attachs;
            files.push(data);
            this.setState({
                selectedAttach: AttachType.Image,
                record: this.updateRecord({ attachs: files })
            });
        }
        else if (at == AttachType.Video)
            this.setState({
                selectedAttach: AttachType.Video,
                record: this.updateRecord({ attachs: [data] })
            });
    }

    ///
    private handleUploadRemove(e: FileThumbnaildGetDto | ContentMetadata) {

        if ((e as FileThumbnaildGetDto).mainFile) {
            this.setState({
                selectedAttach: null,
                record: this.updateRecord({ attachs: null })
            });
        }
    }

    ///
    private handleChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
        var v = event.target.value;
        switch (event.target.name) {
            case "title":
                this.setState({
                    record: this.updateRecord({ title: v }),
                    validation: { ...this.state.validation, title: v.length > 0 }
                });
                break;
        }
    }

    ///
    private handleUploadingStatus = (status: UploadingStatus) => {
        this.setState({ uploadingStatus: status });
    };

    ///
    private canGoStep2(): boolean {
        const { record, validation } = this.state;
        return validation.title && record.isPublic;
    }

    ///
    private canSave(): boolean {
        const { record, validation } = this.state;

        let result = validation.title && this.state.uploadingStatus != "uploading";

        if (record.isPublic) {
            result = result
                &&
                validation.topics
                &&
                (record.advOptions.enabled === false || (validation.period === true && validation.hightLightUntil === true));
        }

        return result;
    }

    ///
    private load(publicationId: string) {
        this.setState({ formCycle: RequestLifeCycle.Loading });
        pubApi.getList(publicationId)
            .then((record) => {

                var selectedAttach = null;
                if (record.attachs.length > 0) {
                    selectedAttach = MediaUtils.getAttachTypeFromMimeType(record.attachs[0].mainFile.mimeType);
                }

                this.setState({
                    formCycle: RequestLifeCycle.None,
                    record,
                    selectedAttach,
                    step: record.editPermissions == PublicationEditPermissions.None ? 0 : 1,
                    validation: {
                        topics: true,
                        title: true,
                        period: true,
                        hightLightUntil: true
                    }
                });
            })
            .catch((error) => {
                this.setState({ lastError: error, formCycle: RequestLifeCycle.LoadFailed });
            });
    }

    ///
    private save() {
        const { record } = this.state;

        const dto: SaveListDto = {
            publicationId: record.publicationId,
            title: record.title,
            contentBody: record.contentBody,
            attachs: record.attachs
                ? record.attachs.map((v) => {
                    return { fileId: v.mainFile.fileId, thumbnailId: v.thumbnail == null ? null : v.thumbnail.fileId };
                })
                : null,
            topics: record.isPublic ? record.topics.filter(p => !p.fixed).map((v) => { return v.key; }) : [],
            relativesTopics: record.isPublic ? record.relativesTopics?.map((v) => { return v.key; }) : [],
            advOptions: record.isPublic
                ? record.advOptions
                : {
                    enabled: false,
                    isInstitutional: false,
                    viewPeriodStart: null,
                    viewPeriodEnd: null,
                    viewHighlightUntil: null,
                    viewPriority: null,
                },
            isPublic: record.isPublic,
            allowSuggestions: record.allowSuggestions
        };

        this.setState({ formCycle: RequestLifeCycle.Posting });

        pubApi.saveList(dto)
            .then((result) => {

                if (record.publicationId == null) {
                    // Reseta lista do sidebar.
                    this.props.reset();
                    // Abre página secundária para adição de itens.
                    getPublicationSecondViewAction(result.publicationType, result.publicationId)();
                }
                else if (result && result.publicationId && record.owner && record.owner.id == Session.user.id) // Atualiza Nome na lista do sidebar.
                    this.props.changeName(RoutePaths.TimelineList.replace(":id", record.publicationId), record.title);

                this.props.onClose(result);
            })
            .catch((error) => {
                this.setState({ lastError: error, formCycle: RequestLifeCycle.PostFailed });
            });

    }

    ///
    private goPrivateList() {
        this.setState({
            record: this.updateRecord({ isPublic: false })
        });
    }

    ///
    private goPublicList() {
        this.setState((state) => {
            state.record.isPublic = true;
            state.validation.topics = state.record.topics.length > 0;
            return state;
        });
    }

    ///
    private loadPrediction() {
        if (this.state.step != 2) return;

        const { title, contentBody, publicationId, topics } = this.state.record;
        if (publicationId != null || topics?.length > 0) return;

        this.setState({ formCycle: RequestLifeCycle.Loading },
            () => {
                pubApi.getTopicPredictions(title, contentBody)
                    .then((result) => {
                        if (result) {
                            this.setState({
                                formCycle: RequestLifeCycle.None,
                                record: this.updateRecord({ topics: result.topics, relativesTopics: result.relativesTopics, deletedRelativesTopics: [] }),
                                validation: { ...this.state.validation, topics: true }
                            }, () => result?.topics.length > 0 && this.props.snackbar.showMessage(this.props.t("Views.Publication.Common.Form.TopicSuggestionFound"), "info"));
                        }
                        else {
                            this.setState({
                                formCycle: RequestLifeCycle.None
                            });
                        }
                    })
                    .catch((error) => {
                        this.setState({
                            formCycle: RequestLifeCycle.None
                        });
                    });
            });
    }

    ///
    private goStep1() {
        this.setState({ step: 1 });
    }

    ///
    private goStep2() {
        if (this.canGoStep2()) {
            this.setState({
                step: this.state.step + 1
            }, () => this.loadPrediction());
        }
    }

    ///
    private step1() {
        const { classes } = this.props;
        const { record, validation } = this.state;
        const { editPermissions } = record;

        let dropzoneMessage = this.props.t("Views.Publication.CreateList.DropzoneImageOnlyText");
        const acceptFileTypes = [AttachType.Image];
        if (Session.hasRole(SystemRoles.PublisherVideo)) {
            acceptFileTypes.push(AttachType.Video);
            dropzoneMessage = this.props.t("Views.Publication.CreateList.DropzoneText");
        }

        return (
            <>
                <DialogContent className={classes.dialogContent}>

                    <div className={classes.container}>

                        <div>

                            <div className={classes.inputBlock}>
                                <TextField
                                    autoFocus
                                    fullWidth
                                    name="title"
                                    placeholder={this.props.t("Views.Publication.CreateList.TitlePlaceholder")}
                                    autoComplete="off"
                                    value={record.title}
                                    error={validation.title === false}
                                    inputProps={{ maxLength: 255 }}
                                    InputProps={{ readOnly: editPermissions != PublicationEditPermissions.Full }}
                                    onChange={(e) => { this.handleChange(e); }}
                                />
                            </div>

                            <Grid
                                container
                                spacing={3}
                                direction="row"
                            >
                                <Grid item>

                                    <AttachSelection
                                        acceptedTypes={acceptFileTypes}
                                        aspectRatio={AspectRatio.Square}
                                        attach={record.attachs}
                                        dropZoneText={dropzoneMessage}
                                        onUploadComplete={(e) => this.handleFileUploaded(e)}
                                        onUploadRemove={(e) => this.handleUploadRemove(e)}
                                        onUploadStatusChange={this.handleUploadingStatus}
                                    />

                                    <FormGroup row>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={record.isPublic}
                                                    onChange={() => record.isPublic ? this.goPrivateList() : this.goPublicList()}
                                                    value="public"
                                                    color="secondary"
                                                />
                                            }
                                            label={this.props.t<string>("Views.Publication.CreateList.PublicListLabel")}
                                        />
                                    </FormGroup>

                                </Grid>
                                <Grid item style={{ flexGrow: 1 }}>
                                    <TextFieldCounter
                                        fullWidth
                                        multiline
                                        placeholder={this.props.t("Views.Publication.CreateList.DescriptionPlaceholder")}
                                        value={record.contentBody}
                                        maxLength={MaxChars}
                                        readOnly={editPermissions != PublicationEditPermissions.Full}
                                        rows={4}
                                        onChange={
                                            (v) => {
                                                this.setState({
                                                    record: this.updateRecord({ contentBody: v })
                                                });
                                            }
                                        }
                                    />
                                </Grid>
                            </Grid>

                        </div>
                    </div>
                </DialogContent>

                <DialogActions>

                    {
                        record.isPublic && this.state.record.publicationId == null &&
                        <div className={classes.footerMessage}>
                            <b>{this.props.t("Views.Publication.CreateList.Footer1")}</b>
                            <span>&nbsp;&raquo;&nbsp;</span>
                            <span>{this.props.t("Views.Publication.Common.Form.Footer2")}</span>
                        </div>
                    }

                    {
                        !record.isPublic &&
                        <MuiPushRight>
                            <ButtonSave
                                color="secondary"
                                onClick={() => this.save()}
                                disabled={!this.canSave()}
                            />
                        </MuiPushRight>
                    }

                    {
                        record.isPublic &&
                        <MuiRegularButton
                            variant="contained"
                            color="primary"
                            disabled={!this.canGoStep2()}
                            onClick={() => this.goStep2()}
                        >
                            <span>{this.props.t("Views.Publication.Common.Form.ButtonContinueText")}</span>
                        </MuiRegularButton>
                    }
                </DialogActions>
            </>
        );
    }

    ///
    private step2() {
        const { classes } = this.props;
        const { record, validation } = this.state;
        const saveText = this.props.t(record.publicationId == null ? "Views.Publication.CreateList.SaveTextNew" : "Views.Publication.CreateList.SaveTextEdit");

        return (
            <>
                <DialogContent>
                    <div className={classes.container}>

                        <TopicsAndMembersBox
                            skipTopicAccessFor={SystemRoles.PublicationManager}
                            topics={record.topics}
                            relativesTopics={record.relativesTopics}
                            deletedRelativesTopics={record.deletedRelativesTopics}
                            findRelativesTopics={record.publicationId == null}
                            onChange={
                                (topics, relativesTopics, deletedRelativesTopics) => {
                                    this.setState({
                                        record: this.updateRecord({ topics, relativesTopics, deletedRelativesTopics }),
                                        validation: { ...this.state.validation, ...{ topics: topics.length > 0 } }
                                    });
                                }
                            }
                        />

                        <FormGroup row>
                            <FormControlLabel
                                control={
                                    <Switch
                                        name="allowSuggestions"
                                        checked={record.allowSuggestions}
                                        onChange={
                                            (e) => {
                                                this.setState({ record: this.updateRecord({ allowSuggestions: (e.target as HTMLInputElement).checked }) });
                                            }
                                        }
                                        value="institutional"
                                        color="secondary"
                                    />
                                }
                                label={this.props.t<string>("Views.Publication.CreateList.SuggestionLabel")}
                            />
                        </FormGroup>

                        <PublicationAdvancedOptions
                            classes={this.props.classes}
                            onUpdateState={({ advOptions, validation }) => {
                                this.setState({
                                    record: this.updateRecord({ advOptions }),
                                    validation: { ...this.state.validation, ...validation }
                                });
                            }}
                            advOptions={record.advOptions}
                            validation={validation}
                        />

                    </div>

                </DialogContent>

                <DialogActions>
                    <MuiRegularButton variant="contained" onClick={() => this.goStep1()} >
                        <span>{this.props.t("Views.Publication.Common.Form.ButtonBackText")}</span>
                    </MuiRegularButton>

                    <MuiRegularButton
                        variant="contained"
                        color="secondary"
                        style={{ marginLeft: "auto" }}
                        onClick={() => this.save()}
                        disabled={!this.canSave()}
                    >
                        <span>{saveText}</span>
                    </MuiRegularButton>
                </DialogActions>
            </>
        );
    }

    ///
    public componentDidMount() {
        if (this.props.publicationId != null) {
            this.load(this.props.publicationId);
        }
    }

    ///
    public render() {
        const { formCycle, lastError, step } = this.state;

        return (
            <Dialog
                open={true}
                maxWidth={"md"}
                fullWidth
            >
                <DialogTitle>
                    <Grid container justifyContent="space-between">
                        <Grid item>
                            <Typography variant="h6">
                                {this.props.t(this.props.publicationId ? "Views.Publication.CreateList.TitleEdit" : "Views.Publication.CreateList.TitleNew")}
                            </Typography>
                        </Grid>
                        <Grid item>
                            <IconButtonClose
                                color="inherit"
                                size="small"
                                onClick={() => this.props.onClose(null)}
                            />
                        </Grid>
                    </Grid>
                </DialogTitle>

                {
                    formCycle == RequestLifeCycle.LoadFailed
                        ? (
                            <DialogContent className={this.props.classes.dialogContent}>
                                <RequestErrorReport error={lastError} />;
                            </DialogContent>
                        )
                        : step == 0
                            ? (
                                <DialogContent className={this.props.classes.dialogContent}>
                                    <AppMessageBox resource="PublicationEditPermissionDenied" />
                                </DialogContent>
                            )
                            : step == 1
                                ? this.step1()
                                : step == 2
                                    ? this.step2()
                                    : null
                }

                <RequestIndicator type="request" open={formCycle == RequestLifeCycle.Loading} />
                <RequestIndicator type="post" open={formCycle == RequestLifeCycle.Posting} />

                <RequestErrorDialog
                    open={formCycle == RequestLifeCycle.PostFailed}
                    error={lastError}
                    onClose={() => this.setState({ lastError: null, formCycle: RequestLifeCycle.None })}
                />

            </Dialog>
        );
    }
}

///
function mapDispatchToProps(dispatch: ThunkDispatch<ApplicationState, void, Action>): IDispatchProps {
    return bindActionCreators(SideBarActions, dispatch);
}

export default connect<{}, IDispatchProps>(null, mapDispatchToProps)(withSnackbar(withTranslation()(withStyles(styles)(CreateList))));